import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode, Constructor } from '@dojo/framework/core/interfaces';
import { Store } from '@dojo/framework/stores/Store';

// import { Button } from '@dojo/widgets/button';

import { createNlsMiddleware, userstate } from 'lcc-state';
// import { logQuiz } from 'logger';

import { createProgressionMiddleware, BaseProperties } from '../middleware/progression';
import { QuizRPC, QuestionQueueType, QuizMeta } from '../interfaces';
import { quizstate, quizChange, setQuestion, setProgressionIsrunning, infoChange } from '../support/state';

import { QuestionQueue } from '../support/questionqueue';
import { ScoreRollup } from '../support/score-rollup';

import { svc } from '../support/ss-svc';
import { svc as quizsvc } from '../support/quizsvc';

import css from '../css/singlestep.m.css';

interface SingleStepIcache {
	inited: boolean;
	started: boolean;
}

export interface SingleStepProperties {
	quizstore: Store<QuizMeta>;
}

interface AllProps extends BaseProperties, SingleStepProperties, WidgetProperties {
	question: any;
}

const nls = createNlsMiddleware('quiz');
const progression = createProgressionMiddleware();
const icache = createICacheMiddleware<SingleStepIcache>();
const factory = create({ icache, nls, theme, progression, quizstate, userstate }).properties<SingleStepProperties>();

export const SingleStep = factory(
	({ properties, middleware: { icache, nls, theme, progression, quizstate, userstate } }) => {
		icache.getOrSet('inited', () => {
			const { quizstore } = properties();
			quizstate.setStore(quizstore);
			progression.setStore(quizstore);
			progression.initialize();
			progression.svc = svc;
			return true;
		});

		const props = () => {
			const { get, path } = quizstate;
			const theProps: Partial<AllProps> = Object.assign({}, progression.getProperties(), properties(), {
				question: get(path('question'))
			});
			return theProps as AllProps;
		};

		const themeCss = theme.classes(css);

		const finalize = async () => {
			const { get, path } = userstate;
			const { svc } = progression;
			const userid = get(path('user_id'));
			quizstate.run(setProgressionIsrunning)({ isrunning: 'final' });

			const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
			icache.delete('started');
			quizstate.run(quizChange)({ quiz });
			quizstate.run(infoChange)({ info });

			quizstate.run(setQuestion)({ question: undefined });

			await quizsvc.update_annotation({ userid });
			return true;
		};
		progression.finalize = finalize;

		/* --- not used ---
		const buttons = (): DNode[] => {
			const { quizid } = props();
			const {
				messages: { btnSave_close = 'Save and close', btnSubmit = 'Submit' }
			} = nls();

			return [
				v('div', { xqa: 'action single', classes: [themeCss.oneaction] }, [
					w(
						Button,
						{
							key: 'save',
							onClick: () => {
								logQuiz.info(`save and close ${quizid}`);
							}
						},
						[btnSave_close]
					),
					w(
						Button,
						{
							key: 'submit',
							onClick: () => {
								logQuiz.info(`submit ${quizid}`);
							}
						},
						[btnSubmit]
					)
				])
			];
		};
		*/

		const render_page = (): DNode[] => {
			const {
				isPlaceholder,
				messages: { discActivity, memoActivity, memoFeedbackHead = '[quiz] memoFeedbackHead' }
			} = nls();
			if (isPlaceholder) {
				return [];
			}
			const { quiz = {} as QuizRPC, quizstore, info, question } = props();
			const { finalize, startquiz, svc } = progression;
			const activityHead = question && question.questiontype === 'Discussion' ? discActivity : memoActivity;
			let showFeedback = false;
			let feedback = '';
			if (info && question) {
				const { start_state } = info;
				showFeedback =
					question.questiontype !== 'Discussion' && (start_state === 'posted' || start_state === 'graded');
				if (showFeedback) {
					feedback = question.feedback;
				}
			}

			icache.getOrSet('started', async () => {
				const { get, path } = quizstate;
				let info = get(path('info'));
				if (!info || info.numberofquestions === 0) {
					await progression.load_quiz();
					info = get(path('info'));
				}
				const status = info.start_state;
				progression.startquiz = status === 'posted' ? await svc.continue_quiz() : await svc.start_quiz();
				await progression.quiz_startup();
				return true;
			});

			if (!icache.get('started')) {
				return [];
			}

			return [
				showFeedback
					? v('div', { key: 'feedback', classes: [themeCss.feedback] }, [
							v('h4', { key: 'feedback-head' }, [memoFeedbackHead]),
							v('div', { key: 'feedback-body', innerHTML: feedback })
							// tslint:disable-next-line:indent
					  ])
					: null,
				v('div', { key: 'static' }, [
					v('h4', { classes: [themeCss.activityHead], innerHTML: activityHead }),
					v('div', { classes: [themeCss.instructions], innerHTML: question ? question.text : '' })
				]),
				v('div', { key: 'content', classes: [themeCss.questionBlock] }, [
					w<QuestionQueueType>(QuestionQueue, {
						key: `${quiz.id}-queue`,
						svc,
						finalize,
						startquiz,
						quizstore
					})
				])
			];
		};

		const { quiz, info, key, quizstore } = props();
		const { load_quiz, render_loading } = progression;
		const hasdata = quiz && info;
		!hasdata && load_quiz();

		return v(
			'div',
			{ key, xqa: `progression single-step`, classes: [themeCss.root] },
			hasdata ? [w(ScoreRollup, { key: `rollup-${key}`, quizstore } as any, render_page())] : render_loading()
		);
	}
);

export default SingleStep;
